import type { ReactNode } from "react";
import type { SiteDocument, Href, SiteNode } from "@zirro/schema";
import type { InspectorField } from "./inspector";

/** PRD §81–83: one render engine for all three surfaces. */
export type RenderMode = "edit" | "preview" | "public";

/** Breakpoints (PRD §48). Desktop is the base (no override bucket). */
export type Breakpoint = "desktop" | "tablet" | "mobile";

export const BREAKPOINTS: Record<Breakpoint, { min: number; max: number }> = {
  desktop: { min: 1024, max: Infinity },
  tablet: { min: 768, max: 1023 },
  mobile: { min: 0, max: 767 },
};

export function breakpointForWidth(width: number): Breakpoint {
  if (width >= 1024) return "desktop";
  if (width >= 768) return "tablet";
  return "mobile";
}

export interface RenderContext {
  mode: RenderMode;
  document: SiteDocument;
  /** Page currently being rendered — used to resolve internal links. */
  pageId?: string;
  /** Editor viewport override; when absent, responsive CSS media queries apply. */
  breakpoint?: Breakpoint;
  /** Currently selected node ID on the canvas. */
  selectedNodeId?: string | null;
  /** Callback when user selects a node on the canvas. */
  onSelectNode?: (nodeId: string | null) => void;
  /** Currently hovered node ID. */
  hoveredNodeId?: string | null;
  /** Callback when user hovers a node on the canvas. */
  onHoverNode?: (nodeId: string | null) => void;
  /** Node ID currently undergoing direct inline editing. */
  inlineEditingNodeId?: string | null;
  /** Callback to set inline editing node ID. */
  onInlineEditChange?: (nodeId: string | null) => void;
  /** Callback to update node props directly from inline canvas editing. */
  onUpdateNodeProps?: (nodeId: string, props: Record<string, unknown>) => void;
  /** Intercept internal navigation (editor canvas / SPA). */
  onNavigate?: (href: Href) => void;
  /**
   * Replaces a node with live content when it returns something other than
   * undefined. Online stores use it to fill "slot" containers (ids ending in
   * `__products` or `__catalog`) with real products; elsewhere the node's
   * own children render as an editable placeholder.
   */
  renderSlot?: (node: SiteNode) => ReactNode | undefined;
  /** Extra controls in the site header, e.g. a store's cart and wishlist. */
  headerActions?: ReactNode;
  /**
   * Set while rendering the children of a flex column (a vertical Section or
   * Container). There the parent's `align` decides each child's horizontal
   * position, so children with their own alignment must claim it themselves.
   */
  inFlexColumn?: boolean;
}

export interface NodeRendererProps {
  node: SiteNode;
  ctx: RenderContext;
}

export interface RegistryEntry {
  type: string;
  label: string;
  isContainer: boolean;
  /** Parent contexts this node may appear under (PRD §28). */
  allowedParents: string[];
  /** Default props from the canonical component rules (PRD §27). */
  defaultProps: Record<string, unknown>;
  /** JSON Schema fragment for this component's props (from site.schema.json). */
  propsSchema: Record<string, unknown>;
  /** Inspector field descriptors for the editor (PRD §27, §29–34). */
  inspector: InspectorField[];
  Component: (props: NodeRendererProps) => React.ReactElement | null;
}
